/**
 * Scroll an element into view and ring it briefly, so a host who was sent somewhere can see where
 * they landed.
 *
 * <p>Two callers want exactly this and for the same reason. A {@code /profile#thing} deep link
 * lands on a long page where the thing it named is one card among many. And the delivery switch's
 * "Connect a calendar" points at a panel in the sticky sidebar that is usually <em>already on
 * screen</em> — which is precisely why scrolling alone is not enough and this exists: without the
 * ring, pressing it looks like pressing nothing.</p>
 *
 * <p>The ring is the part that must survive {@code prefers-reduced-motion}; only the glide is
 * dropped. It sets no radius of its own, so the ring follows the element's and a card stays a card.
 * Returns a cancel function — leaving mid-flash would otherwise strand the ring on the element.</p>
 */
const FLASH = ["ring-2", "ring-brand-blue", "ring-offset-2", "ring-offset-background"];

/** How long the ring stays on. Long enough to find, short enough not to become decoration. */
export const FLASH_MS = 1600;

export function flashElement(
  el: HTMLElement,
  { block = "start" as ScrollLogicalPosition, ms = FLASH_MS } = {},
): () => void {
  const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  el.scrollIntoView({ behavior: reduced ? "auto" : "smooth", block });
  el.classList.add(...FLASH);
  const timer = window.setTimeout(() => el.classList.remove(...FLASH), ms);
  return () => {
    window.clearTimeout(timer);
    el.classList.remove(...FLASH);
  };
}

/** The same, by id. No-ops (returning null) when nothing by that id is on the page. */
export function flashElementById(
  id: string,
  opts?: { block?: ScrollLogicalPosition; ms?: number },
): (() => void) | null {
  const el = document.getElementById(id);
  return el ? flashElement(el, opts) : null;
}
